import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, inject, OnDestroy, OnInit, PLATFORM_ID, signal, viewChild } from '@angular/core';
import { ButtonComponent, DialogComponent, TextDirective } from '@joii/design-system';
import { LucideBell, LucideDynamicIcon, LucideMonitorDown } from '@lucide/angular';
import { UserStore } from 'src/shared/stores/user.store';
import { PUSH_OPTED_OUT_KEY, PushNotificationService } from '../../services/push-notification.service';
import { PwaInstallService } from '../../services/pwa-install.service';

const PWA_BANNER_DISMISSED_KEY = 'trials_pwa_banner_dismissed';

function isPwaInstalled(): boolean {
  if (typeof window === 'undefined') return true;
  // iOS Safari (pre-15.4)
  if ((navigator as { standalone?: boolean }).standalone) return true;
  // Standalone, fullscreen, or minimal-ui
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.matchMedia('(display-mode: minimal-ui)').matches
  );
}

function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

@Component({
  selector: 'app-pwa-install-banner',
  standalone: true,
  imports: [ButtonComponent, DialogComponent, TextDirective, LucideDynamicIcon],
  template: `
    @if (showBanner()) {
      <joii-dialog
        #dialogRef
        variant="flexible-sized-responsive"
        [headerOptions]="{ title: 'Please note', closeButton: true }"
        (closeClick)="dismiss()"
      >
        <div class="pwa-dialog-content">
          @if (showAddToHomeScreen()) {
            <div class="pwa-dialog-section">
              <div class="pwa-dialog-icon">
                <svg [lucideIcon]="installIcon" [size]="24" />
              </div>
              <div class="pwa-dialog-text">
                <p class="pwa-dialog-title" joii-text [size]="6" [weight]="'semibold'">Download the app version</p>
                <p class="pwa-dialog-desc" joii-text [size]="5" color="base">
                  @if (isIos) {
                    <!-- IOS does not support the install prompt, so we need to tell the user to do it manually-->
                    Tap the Share button in the address bar (bottom of Safari), then "Add to Home Screen"
                  } @else if (deferredPrompt()) {
                    Install the app for quick access
                  } @else {
                    Click the install icon in your browser's address bar
                  }
                </p>
              </div>
              @if (deferredPrompt()) {
                <button
                  joii-button
                  variant="secondary"
                  [loading]="installTriggering()"
                  (click)="triggerInstall()"
                  [disabled]="installTriggering()"
                  class="pwa-dialog-action"
                >
                  {{ installTriggering() ? 'Opening…' : 'Install' }}
                </button>
              }
            </div>
          }

          @if (showNotificationPrompt()) {
            <div class="pwa-dialog-section">
              <div class="pwa-dialog-icon">
                <svg [lucideIcon]="bellIcon" [size]="24" />
              </div>
              <div class="pwa-dialog-text">
                <p class="pwa-dialog-title" joii-text [size]="6" [weight]="'semibold'">Get notified</p>
                <p class="pwa-dialog-desc" joii-text [size]="5" color="base">
                  @if (notificationDenied()) {
                    Notifications are blocked. Enable them in your browser settings.
                  } @else if (isIos && !isPwaInstalledForNotifications()) {
                    Add to Home Screen first (Share → Add to Home Screen), then open from there to enable notifications.
                  } @else {
                    Get notified about when your clinician joins the video calls
                  }
                </p>
              </div>
              @if (!(isIos && !isPwaInstalledForNotifications())) {
                <button
                  joii-button
                  variant="secondary"
                  [loading]="notificationRequesting()"
                  [disabled]="notificationRequesting()"
                  (click)="onNotificationClick()"
                  class="pwa-dialog-action"
                >
                  Enable
                </button>
              }
            </div>
          }
        </div>
      </joii-dialog>
    }
  `,
  styles: `
    .pwa-dialog-content {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 8px 0 24px;
      max-width: 517px;
    }

    .pwa-dialog-section {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 10px 12px;
      background: var(--Vetai-Form_controls--Background_Default, #fdfdfc);
      border-radius: 8px;
    }

    .pwa-dialog-icon {
      flex-shrink: 0;
      width: 56px;
      height: 56px;
      min-width: 56px;
      border-radius: 100px;
      background: var(--Vetai-Backgrounds--Highlight);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .pwa-dialog-text {
      flex: 1;
      min-width: 0;
    }

    .pwa-dialog-title {
      margin: 0 0 4px 0;
    }

    .pwa-dialog-desc {
      margin: 0;
      color: var(--Vetai-Texts--Tint_2, #576275);
    }

    .pwa-dialog-action {
      flex-shrink: 0;
    }
  `,
})
export class PwaInstallBannerComponent implements OnInit, AfterViewInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private pushNotificationService = inject(PushNotificationService);
  private userStore = inject(UserStore);
  pwaInstallService = inject(PwaInstallService);

  dialogRef = viewChild<DialogComponent>('dialogRef');

  installIcon = LucideMonitorDown;
  bellIcon = LucideBell;

  showBanner = signal(false);
  showAddToHomeScreen = signal(false);
  showNotificationPrompt = signal(false);
  deferredPrompt = this.pwaInstallService.deferredPrompt;
  notificationRequesting = signal(false);
  installTriggering = signal(false);
  notificationGranted = signal(false);
  notificationDenied = signal(false);
  pushOptedOut = signal(false);

  private _isIos = false;
  private _isInstalled = true;
  private permissionPollId: ReturnType<typeof setInterval> | null = null;

  get isIos(): boolean {
    return this._isIos;
  }

  /** On iOS, Web Push only works when the app is added to Home Screen (standalone). */
  isPwaInstalledForNotifications(): boolean {
    return this._isInstalled;
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this._isInstalled = isPwaInstalled() || this.pwaInstallService.appInstalled();
    this._isIos = isIos();

    const dismissed = localStorage.getItem(PWA_BANNER_DISMISSED_KEY) === 'true';
    const needsInstall = !this._isInstalled;
    const perm = this.notificationPermission();
    const needsNotifications = perm !== 'granted';

    this.showAddToHomeScreen.set(needsInstall);
    this.showNotificationPrompt.set(needsNotifications);
    this.showBanner.set(!dismissed && (needsInstall || needsNotifications));
    this.updateNotificationPermissionSignals();
    this.pushOptedOut.set(localStorage.getItem(PUSH_OPTED_OUT_KEY) === 'true');

    // Poll for permission changes that can happen outside this banner.
    if (needsNotifications) {
      this.startPermissionPolling();
    }
  }

  ngAfterViewInit(): void {
    if (this.showBanner() && this.dialogRef()) {
      this.dialogRef()!.open();
    }
  }

  ngOnDestroy(): void {
    this.stopPermissionPolling();
  }

  private startPermissionPolling(): void {
    this.stopPermissionPolling();
    this.permissionPollId = setInterval(() => {
      this.updateNotificationPermissionSignals();
      if (this.notificationPermission() === 'granted') {
        this.notificationRequesting.set(false);
        this.updateVisibility();
        this.stopPermissionPolling();
      }
    }, 500);
  }

  private updateNotificationPermissionSignals(): void {
    const perm = this.notificationPermission();
    this.notificationGranted.set(perm === 'granted');
    this.notificationDenied.set(perm === 'denied');
  }

  private stopPermissionPolling(): void {
    if (this.permissionPollId != null) {
      clearInterval(this.permissionPollId);
      this.permissionPollId = null;
    }
  }

  private notificationPermission(): NotificationPermission {
    if (typeof Notification === 'undefined') return 'denied';
    return Notification.permission;
  }

  private async updateVisibility(): Promise<void> {
    this._isInstalled = isPwaInstalled() || this.pwaInstallService.appInstalled();
    if (this._isInstalled) {
      this.showAddToHomeScreen.set(false);
    }
    const needsInstall = !this._isInstalled && this.showAddToHomeScreen();
    const perm = this.notificationPermission();
    const needsNotifications = perm !== 'granted';
    this.showNotificationPrompt.set(needsNotifications);
    const dismissed = localStorage.getItem(PWA_BANNER_DISMISSED_KEY) === 'true';
    const shouldShow = !dismissed && (needsInstall || needsNotifications);
    if (!shouldShow && this.showBanner()) {
      await this.dialogRef()?.close();
    }
    this.showBanner.set(shouldShow);
  }

  async triggerInstall(): Promise<void> {
    const prompt = this.deferredPrompt();
    if (prompt) {
      this.installTriggering.set(true);
      try {
        const accepted = await this.pwaInstallService.promptInstall();
        if (accepted) {
          this._isInstalled = true;
        }
      } finally {
        this.installTriggering.set(false);
      }
      this.updateVisibility();
      return;
    }
    // iOS: Add to Home Screen only appears in Safari's address bar Share menu, not in
    // navigator.share(). The banner instructions point users to the address bar. No action needed.
  }

  async onNotificationClick(): Promise<void> {
    this.notificationRequesting.set(true);
    this.pushNotificationService.registerIfPossible(this.userStore.user()?.doc_id ?? '');
    // Reset after a delay (permission poll will also clear when granted)
    setTimeout(() => this.notificationRequesting.set(false), 3000);
  }

  dismiss(): void {
    localStorage.setItem(PWA_BANNER_DISMISSED_KEY, 'true');
    // Delay removal so the dialog's close animation completes and body scroll lock is released
    setTimeout(() => this.showBanner.set(false), 300);
  }
}
